import { useCallback } from 'react';
import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  createProcess,
  deleteProcess,
  duplicateProcess,
  patchProcess,
  type CreateProcessRequest,
  type DuplicateProcessRequest,
  type ProcessDetailDto,
  type ProcessListResponse,
} from '@api/processes';
import { processesListRootKey } from './useProcesses';

type ProcessesListCache = InfiniteData<ProcessListResponse, number>;

interface RenameVars {
  processId: string;
  name: string;
}

interface RenameContext {
  /** Snapshot of every list cache before the optimistic mutation. */
  previous: { queryKey: readonly unknown[]; data: ProcessesListCache }[];
}

/** Default soft-delete window — matches the snackbar's auto-fade duration. */
const DEFAULT_REMOVE_DELAY_MS = 5_000;

interface RemoveWithUndoOptions {
  /**
   * Hold the optimistic-removal in the cache for this long before actually
   * firing the DELETE. When the snackbar's `Undo` button is clicked we cancel
   * the timer + restore the cache snapshots, so no API call is made at all.
   * Defaults to {@link DEFAULT_REMOVE_DELAY_MS} (5s — matches the snackbar
   * auto-fade window).
   */
  delayMs?: number;
}

export interface RemoveWithUndoHandle {
  /**
   * Cancel the pending DELETE and restore every cache snapshot. Safe to call
   * after the timer has fired — becomes a no-op once the request is in flight
   * (so a late Undo click can't double-restore or race the in-flight delete).
   */
  undo: () => void;
  /**
   * Resolves with `true` once the server has confirmed the delete, with
   * `false` if the user undid before the DELETE was dispatched, and rejects
   * with the server error if the DELETE fails. Designed so callers can
   * `.catch()` only on real errors and ignore the promise otherwise.
   */
  committed: Promise<boolean>;
}

const cloneListCacheWithoutRow = (
  data: ProcessesListCache,
  processId: string,
): ProcessesListCache => ({
  ...data,
  pages: data.pages.map((page) => {
    const before = page.processes.length;
    const filtered = page.processes.filter((p) => p.processId !== processId);
    if (filtered.length === before) return page;
    const removed = before - filtered.length;
    return {
      ...page,
      processes: filtered,
      totalElements: Math.max(0, page.totalElements - removed),
    };
  }),
});

/**
 * Return a new list page with the row matching `processId` renamed to
 * `trimmedName`. Pages that don't contain the row are returned unchanged
 * (referential identity preserved so React Query memoises correctly).
 */
const renamePageRow = (
  page: ProcessListResponse,
  processId: string,
  trimmedName: string,
): ProcessListResponse => {
  if (!page.processes.some((p) => p.processId === processId)) return page;
  return {
    ...page,
    processes: page.processes.map((p) =>
      p.processId === processId ? { ...p, name: trimmedName } : p,
    ),
  };
};

/**
 * Apply `renamePageRow` across every page in an InfiniteData cache. Kept
 * as a top-level helper so the rename `onMutate` callback stays shallow
 * (Sonar caps function nesting at 4).
 */
const cloneListCacheWithRenamedRow = (
  data: ProcessesListCache,
  processId: string,
  trimmedName: string,
): ProcessesListCache => ({
  ...data,
  pages: data.pages.map((page) => renamePageRow(page, processId, trimmedName)),
});

/**
 * Pull the row out of every list page in the cache and return restore
 * snapshots so a later `undo()` can put it back exactly as it was. Pure —
 * does not invalidate / refetch on its own.
 */
const optimisticallyRemoveFromCache = (
  queryClient: QueryClient,
  processId: string,
): { queryKey: readonly unknown[]; data: ProcessesListCache }[] => {
  const snapshots = queryClient.getQueriesData<ProcessesListCache>({
    queryKey: processesListRootKey,
  });
  const previous: { queryKey: readonly unknown[]; data: ProcessesListCache }[] =
    [];
  snapshots.forEach(([queryKey, data]) => {
    if (!data || !Array.isArray(data.pages)) return;
    previous.push({ queryKey, data });
    queryClient.setQueryData<ProcessesListCache>(
      queryKey,
      cloneListCacheWithoutRow(data, processId),
    );
  });
  return previous;
};

/**
 * Mutations for the Processes gallery. Each mutation invalidates the list
 * query family so any active filter/page combination refetches.
 *
 * The hook returns plain `mutateAsync` thunks rather than the full mutation
 * objects so consumers can compose actions (e.g. duplicate-then-toast) without
 * worrying about loading flags they don't need. Loading state is exposed via
 * `isAnyMutationPending` for things like disabling the toolbar while an
 * action is in flight.
 */
export function useProcessActions() {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: processesListRootKey });

  const createMutation = useMutation<
    ProcessDetailDto,
    Error,
    CreateProcessRequest
  >({
    mutationFn: (req) => createProcess(req),
    onSuccess: () => invalidate(),
  });

  const duplicateMutation = useMutation<
    ProcessDetailDto,
    Error,
    { processId: string; body?: DuplicateProcessRequest }
  >({
    mutationFn: ({ processId, body }) => duplicateProcess(processId, body),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (processId) => deleteProcess(processId),
    onSuccess: () => invalidate(),
  });

  /**
   * Rename is the most user-visible mutation in the gallery — Enter on the
   * inline editor should appear instant. We snapshot every active list page,
   * rewrite the row name in-place, then `invalidate()` on settle so the
   * server-confirmed payload (with the new `updatedAt`) becomes the source of
   * truth. On error we restore the snapshots and let the page surface a toast.
   */
  const renameMutation = useMutation<
    ProcessDetailDto,
    Error,
    RenameVars,
    RenameContext
  >({
    // Trim once and use the same value for both the optimistic cache write
    // and the network request — keeps the cached row and the server payload
    // in lockstep (otherwise the post-settle invalidate would surface a
    // visible name flicker if the server preserved the trailing whitespace).
    mutationFn: ({ processId, name }) =>
      patchProcess(processId, { name: name.trim() }),
    onMutate: async ({ processId, name }) => {
      await queryClient.cancelQueries({ queryKey: processesListRootKey });
      const trimmed = name.trim();
      const snapshots = queryClient.getQueriesData<ProcessesListCache>({
        queryKey: processesListRootKey,
      });
      const previous: RenameContext['previous'] = [];
      snapshots.forEach(([queryKey, data]) => {
        if (!data || !Array.isArray(data.pages)) return;
        previous.push({ queryKey, data });
        queryClient.setQueryData<ProcessesListCache>(
          queryKey,
          cloneListCacheWithRenamedRow(data, processId, trimmed),
        );
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => invalidate(),
  });

  /**
   * Optimistic delete with a soft-delete window — yanks the row out of every
   * cached page immediately so the UI updates instantly, then schedules the
   * actual DELETE after `delayMs`. The returned `undo()` cancels the timer
   * and restores the row from snapshots, so an Undo click never reaches the
   * server.
   *
   * Intentionally NOT a `useMutation` — the soft-delete window is a UX
   * pattern, not an in-flight network request, and modelling it as a
   * mutation would surface confusing `isPending` state during the wait.
   */
  const removeWithUndo = useCallback(
    (
      processId: string,
      { delayMs = DEFAULT_REMOVE_DELAY_MS }: RemoveWithUndoOptions = {},
    ): RemoveWithUndoHandle => {
      const previous = optimisticallyRemoveFromCache(queryClient, processId);

      // State machine: at most one of {undone, dispatched} ever flips true;
      // once either does, the other path becomes a no-op. This prevents:
      //   - Undo racing the timer-fired DELETE (Undo restores the row,
      //     DELETE then succeeds and removes it again).
      //   - The committed promise hanging forever after Undo.
      let undone = false;
      let dispatched = false;
      let timerId: ReturnType<typeof setTimeout> | null = null;

      const restoreCache = () => {
        previous.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      };

      let resolveCommitted!: (value: boolean) => void;
      let rejectCommitted!: (reason: Error) => void;
      const committed = new Promise<boolean>((resolve, reject) => {
        resolveCommitted = resolve;
        rejectCommitted = reject;
      });

      const onDeleteSuccess = () => {
        // Fire-and-forget invalidate — failure to refetch is non-fatal,
        // the optimistic write already covered the visible state.
        queryClient
          .invalidateQueries({ queryKey: processesListRootKey })
          .catch(() => undefined);
        resolveCommitted(true);
      };

      const onDeleteFailure = (err: unknown) => {
        restoreCache();
        rejectCommitted(
          err instanceof Error ? err : new Error(String(err)),
        );
      };

      const dispatchDelete = () => {
        timerId = null;
        dispatched = true;
        deleteProcess(processId).then(onDeleteSuccess, onDeleteFailure);
      };

      timerId = setTimeout(dispatchDelete, delayMs);

      const undo = () => {
        // Late Undo (after the delete already fired) is a no-op so we don't
        // double-restore or race the in-flight request.
        if (undone || dispatched) return;
        undone = true;
        if (timerId !== null) {
          clearTimeout(timerId);
          timerId = null;
        }
        restoreCache();
        resolveCommitted(false);
      };

      return { undo, committed };
    },
    [queryClient],
  );

  return {
    create: createMutation.mutateAsync,
    duplicate: duplicateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    removeWithUndo,
    rename: renameMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isRemoving: deleteMutation.isPending,
    isRenaming: renameMutation.isPending,
    isAnyMutationPending:
      createMutation.isPending ||
      duplicateMutation.isPending ||
      deleteMutation.isPending ||
      renameMutation.isPending,
  };
}
